import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogClose } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Calendar, Clock, Users, FileText, Edit, AlertTriangle, CheckCircle, Search, X, Plus, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { showToast } from '../utils/toast';
import { handleAPIError } from '../utils/errorHandler';
import { useAuth } from '../contexts/AuthContext';
import {
  findConflictingParticipants,
  getParticipantAvailability,
  generateConflictMessage,
  formatTime
} from '../utils/meetingValidation';
import { meetingAPI, userAPI } from '../Services/apiService';
import FormField from './FormField';
import { validateMeetingTitle, validateLocation, validateMeetingType, validateDate, validateTimeRange, validateParticipants, validateAgenda } from '../utils/formValidation';
import AddParticipantModal from './AddParticipantModal';
import AgendaUploadModal from './AgendaUploadModal';
import CustomRecurrenceModal from './CustomRecurrenceModal';
import { ConfirmDialog, useConfirmDialog } from './ui/confirm-dialog';
import QuillEditor from './ui/QuillEditor';
import UpdateRecurringMeetingDialog from './UpdateRecurringMeetingDialog';

const EditMeetingModal = ({ open, onClose, meeting, onSubmit, users = [], allowFollowingScope = false }) => {
  const { user: currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [ongoingMeetings, setOngoingMeetings] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false);
  const [emailForNewParticipant, setEmailForNewParticipant] = useState('');
  const [allUsers, setAllUsers] = useState(users); // Includes newly created vendors
  const [frequentParticipants, setFrequentParticipants] = useState([]); // Frequently used participants
  const [originalData, setOriginalData] = useState(null); // Track original meeting data
  const closeConfirm = useConfirmDialog(); // Close confirmation

  // Check if form has unsaved changes
  const hasUnsavedChanges = () => {
    if (!originalData) return false;
    const formDataChanged = JSON.stringify(formData) !== JSON.stringify(originalData);
    const filesChanged = agendaFiles.length > 0;
    return formDataChanged || filesChanged;
  };

  // Handle modal close with confirmation
  const handleModalClose = (openState) => {
    if (!openState && hasUnsavedChanges()) {
      closeConfirm.showConfirm(() => onClose(false));
    } else {
      onClose(openState);
    }
  };

  const [formData, setFormData] = useState({
    title: '',
    scheduled_date: '',
    start_time: '',
    end_time: '',
    location: '',
    meeting_type: 'in-person',
    recurrence: 'none',
    custom_recurrence: null,
    participant_ids: [],
    agenda: ''
  });

  // Validation states
  const [titleValidation, setTitleValidation] = useState({ isValid: true, message: '' });
  const [locationValidation, setLocationValidation] = useState({ isValid: true, message: '' });
  const [meetingTypeValidation, setMeetingTypeValidation] = useState({ isValid: true, message: '' });
  const [dateValidation, setDateValidation] = useState({ isValid: true, message: '' });
  const [timeValidation, setTimeValidation] = useState({ isValid: true, message: '' });
  const [participantsValidation, setParticipantsValidation] = useState({ isValid: true, message: '' });
  const [agendaValidation, setAgendaValidation] = useState({ isValid: true, message: '' });
  const [showAgendaUploadModal, setShowAgendaUploadModal] = useState(false);
  const [agendaFiles, setAgendaFiles] = useState([]); // New files to upload
  const [existingAgendaDocuments, setExistingAgendaDocuments] = useState([]); // Existing uploaded documents
  const [showCustomRecurrenceModal, setShowCustomRecurrenceModal] = useState(false);
  const [showRecurringUpdateDialog, setShowRecurringUpdateDialog] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);

  // Sync allUsers with users prop when modal opens OR users data changes
  // Also merge in meeting.participants to ensure we have full data for all participants
  useEffect(() => {
    if (open && users.length > 0) {
      // Start with users from props
      let mergedUsers = [...users];

      // If meeting has participants array with full data, merge them in
      if (meeting?.participants && meeting.participants.length > 0) {
        const userIds = new Set(mergedUsers.map(u => u.id));

        // Add any participants from meeting that aren't in users array
        meeting.participants.forEach(participant => {
          if (!userIds.has(participant.id)) {
            mergedUsers.push(participant);
          } else {
            // Update existing user with participant data if participant has more complete data
            const existingUserIndex = mergedUsers.findIndex(u => u.id === participant.id);
            const existingUser = mergedUsers[existingUserIndex];

            // If existing user is missing email or full_name but participant has it, use participant data
            if ((!existingUser.email || !existingUser.full_name) && (participant.email || participant.full_name)) {
              mergedUsers[existingUserIndex] = { ...existingUser, ...participant };
            }
          }
        });
      }

      setAllUsers(mergedUsers);
    }
  }, [open, users.length, meeting?.participants]); // Sync when modal opens OR users are loaded OR meeting participants change

  // Memoize users array (only active, non-deleted users)
  // All users can see and select all active users as participants
  // Restriction is only on who can CREATE new users (handled in AddParticipantModal)
  const memoizedUsers = useMemo(() => {
    return allUsers.filter(u => {
      const isActive = u.is_active === true || u.is_active === undefined;
      const notDeleted = !u.is_deleted;
      return isActive && notDeleted;
    });
  }, [allUsers.length, allUsers.map(u => u.id + u.is_active).join(',')]);

  // All users including disabled (for showing already selected participants)
  const allAvailableUsers = useMemo(() => allUsers, [allUsers]);

  // Filter users: show frequent participants by default, selected, or search results
  const filteredUsers = useMemo(() => {
    // If no search query
    if (!searchQuery.trim()) {
      // Show selected participants (may include disabled users or vendors)
      const selectedUsers = allAvailableUsers.filter(u => formData.participant_ids.includes(u.id));

      // If no additional participants beyond meeting's original, show frequent participants
      if (selectedUsers.length === (meeting?.participant_ids?.length || 0) && frequentParticipants.length > 0) {
        // Merge: show selected + frequent (deduplicated)
        const selectedIds = new Set(selectedUsers.map(u => u.id));
        const frequentNotSelected = frequentParticipants.filter(u => !selectedIds.has(u.id));
        return [...selectedUsers, ...frequentNotSelected];
      }

      return selectedUsers;
    }

    // When searching, show ALL matching active users (no pagination, all in one list)
    const query = searchQuery.toLowerCase().trim();
    return memoizedUsers.filter(u => {
      const nameMatch = (u.full_name || '').toLowerCase().includes(query);
      const emailMatch = (u.email || '').toLowerCase().includes(query);
      return nameMatch || emailMatch;
    });
  }, [allAvailableUsers, memoizedUsers, searchQuery, formData.participant_ids, frequentParticipants, meeting?.participant_ids]);

  const recurrenceOptions = [
    { value: 'none', label: 'No Recurrence' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'custom', label: 'Custom' }
  ];

  useEffect(() => {
    if (meeting) {
      // Convert backend custom recurrence pattern to frontend format
      let customRecurrence = null;
      if (meeting.custom_recurrence_pattern || meeting.custom_recurrence) {
        const pattern = meeting.custom_recurrence_pattern || meeting.custom_recurrence;

        // Map: daily -> day, weekly -> week, monthly -> month, yearly -> year
        const frequencyMap = {
          'daily': 'day',
          'weekly': 'week',
          'monthly': 'month',
          'yearly': 'year'
        };

        // Map: on_date -> on (backend to frontend)
        const endTypeMap = {
          'never': 'never',
          'on_date': 'on',
          'after': 'after'
        };

        customRecurrence = {
          repeatEvery: pattern.interval,
          repeatUnit: frequencyMap[pattern.frequency] || pattern.frequency,
          selectedDays: pattern.days_of_week || [],
          endType: endTypeMap[pattern.end_type] || pattern.end_type,
          endDate: pattern.end_type === 'on_date' ? pattern.end_value : null,
          occurrences: pattern.end_type === 'after' ? pattern.end_value : null
        };
      }

      // Derive participant IDs — the calendar endpoint returns `participants` as objects
      // while the meetings list endpoint returns flat `participant_ids`. Support both.
      const derivedParticipantIds =
        meeting.participant_ids && meeting.participant_ids.length > 0
          ? meeting.participant_ids
          : (meeting.participants || []).map(p => p.id).filter(Boolean);

      const initialData = {
        title: meeting.title || '',
        scheduled_date: meeting.scheduled_date || '',
        start_time: meeting.start_time || '',
        end_time: meeting.end_time || '',
        location: meeting.location || '',
        meeting_type: meeting.meeting_type || 'in-person',
        recurrence: meeting.recurrence || 'none',
        custom_recurrence: customRecurrence,
        participant_ids: derivedParticipantIds,
        agenda: meeting.agenda || ''
      };
      setFormData(initialData);
      setOriginalData(initialData); // Track original for change detection

      // Load existing agenda documents
      setExistingAgendaDocuments(meeting.agenda_documents || []);

      // Reset new agenda files when meeting changes
      setAgendaFiles([]);
    }
  }, [meeting]);

  // Load frequent participants when modal opens
  useEffect(() => {
    if (open) {
      console.log('🔍 [EditModal] Loading frequent participants...');
      const loadFrequentParticipants = async () => {
        try {
          const response = await userAPI.getFrequentParticipants();
          console.log('✅ [EditModal] Frequent participants loaded:', response.data);
          setFrequentParticipants(response.data || []);
        } catch (error) {
          // Silent fail - not critical
          console.log('❌ [EditModal] No frequent participants found or error loading them:', error);
          setFrequentParticipants([]);
        }
      };
      loadFrequentParticipants();
    } else {
      // Reset when modal closes
      console.log('[EditModal] Modal closed, resetting');
      setSearchQuery('');
      setFrequentParticipants([]);
    }
  }, [open]);

  // Load ongoing meetings when modal opens
  useEffect(() => {
    if (open) {
      const loadOngoingMeetings = async () => {
        try {
          // OPTIMIZED: Fetch only ongoing meetings from backend instead of all meetings
          const response = await meetingAPI.getMeetings({
            state: 'ongoing',  // Backend filter for ongoing meetings only
            page_size: 100     // Reasonable limit for ongoing meetings
          });

          // Backend already filters by state=ongoing, just use the data directly
          const ongoingMeetings = response.data?.items || response.data || [];

          // Exclude current meeting from conflicts
          const filteredOngoing = ongoingMeetings.filter(m => m.id !== meeting?.id);
          setOngoingMeetings(filteredOngoing);
        } catch (error) {
          handleAPIError(error, 'Failed to load ongoing meetings', false);
        }
      };
      loadOngoingMeetings();

      // Refresh ongoing meetings every 30 seconds
      const interval = setInterval(loadOngoingMeetings, 30000);
      return () => clearInterval(interval);
    }
  }, [open, meeting?.id]);

  // Check if meeting is ongoing (defined early for use in useEffect, using optional chaining for safety)
  const isOngoingMeeting = meeting?.state === 'ongoing' || meeting?.status === 'ongoing';
  const isPendingMeeting = meeting?.state === 'pending' || meeting?.status === 'pending';
  const isUpcomingMeeting = meeting?.state === 'upcoming' || meeting?.status === 'upcoming';
  const isCompletedMeeting = meeting?.state === 'completed' || meeting?.status === 'completed';
  const isCancelledMeeting = meeting?.state === 'cancelled' || meeting?.status === 'cancelled';

  // Check for conflicts whenever form data changes
  useEffect(() => {
    if (formData.scheduled_date && formData.start_time && formData.end_time && formData.participant_ids.length > 0) {
      const allConflicts = findConflictingParticipants(
        formData.participant_ids,
        ongoingMeetings,
        formData.scheduled_date,
        formData.start_time,
        formData.end_time,
        memoizedUsers,
        meeting?.id // Exclude current meeting from conflicts
      );

      // For ongoing meetings, only show conflicts for NEW participants (not existing ones)
      if (isOngoingMeeting && meeting?.participant_ids) {
        const existingParticipantIds = meeting.participant_ids;
        const newParticipants = formData.participant_ids.filter(id => !existingParticipantIds.includes(id));
        // Filter conflicts to only include new participants
        const filteredConflicts = allConflicts.filter(conflict =>
          newParticipants.includes(conflict.user.id)
        );
        setConflicts(filteredConflicts);
      } else {
        // For non-ongoing meetings, show all conflicts
        setConflicts(allConflicts);
      }
    } else {
      setConflicts([]);
    }
  }, [formData.scheduled_date, formData.start_time, formData.end_time, formData.participant_ids, ongoingMeetings, memoizedUsers, meeting?.id, isOngoingMeeting, meeting?.participant_ids]);

  const handleLocationChange = (e) => {
    const value = e.target.value;
    if (value.length <= 100) {
      setFormData(prev => ({ ...prev, location: value }));
      // Only validate location if meeting type is In-Person
      if (formData.meeting_type === 'in-person') {
        setLocationValidation(validateLocation(value));
      } else {
        setLocationValidation({ isValid: true, message: '' });
      }
    }
  };

  const handleMeetingTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      meeting_type: value,
      // Clear location when switching to online
      location: value === 'online' ? '' : prev.location
    }));
    setMeetingTypeValidation(validateMeetingType(value));
    // Reset location validation when switching to Online (location not required)
    if (value === 'online') {
      setLocationValidation({ isValid: true, message: '' });
    } else if (value === 'in-person' && formData.location) {
      // Re-validate location when switching to In-Person
      setLocationValidation(validateLocation(formData.location));
    }
  };

  const handleAgendaChange = (htmlContent) => {
    // QuillEditor passes HTML content directly - no character limit
    console.log('📝 [EditModal] Agenda changed:', htmlContent?.substring(0, 100) + '...');
    setFormData(prev => ({ ...prev, agenda: htmlContent }));
    setAgendaValidation(validateAgenda(htmlContent));
  };


  // Build and validate the meeting payload. Returns the data object or null if validation fails.
  const buildMeetingData = (e) => {
    if (e?.preventDefault) e.preventDefault();

    // Validate form
    if (!formData.title.trim()) {
      setTitleValidation(validateMeetingTitle(formData.title));
      showToast.error('Meeting title is required');
      return null;
    }

    // Only validate location if meeting type is In-Person
    if (formData.meeting_type === 'in-person') {
      const locationValidationResult = validateLocation(formData.location);
      if (!locationValidationResult.isValid) {
        setLocationValidation(locationValidationResult);
        showToast.error(locationValidationResult.message);
        return null;
      }
    }

    const meetingTypeValidationResult = validateMeetingType(formData.meeting_type);
    if (!meetingTypeValidationResult.isValid) {
      setMeetingTypeValidation(meetingTypeValidationResult);
      showToast.error(meetingTypeValidationResult.message);
      return null;
    }

    if (!formData.scheduled_date) {
      showToast.error('Meeting date is required');
      return null;
    }

    // Real-time validation: Different rules for ongoing vs upcoming meetings
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const selectedDate = new Date(formData.scheduled_date);
    const selectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    const currentTimeStr = now.toTimeString().slice(0, 5);

    if (isOngoingMeeting) {
      if (formData.end_time <= currentTimeStr) {
        showToast.error('End time must be after current time to extend the meeting.');
        return null;
      }
    } else if (!isPendingMeeting) {
      if (selectedDateOnly.getTime() === today.getTime()) {
        if (formData.start_time < currentTimeStr) {
          showToast.error('Meeting time has already passed. Please select a future time.');
          return null;
        }
      }
    }

    if (!formData.start_time) {
      showToast.error('Start time is required');
      return null;
    }

    if (!formData.end_time) {
      showToast.error('End time is required');
      return null;
    }

    if (formData.participant_ids.length === 0) {
      showToast.error('At least one participant is required');
      return null;
    }

    if (formData.start_time >= formData.end_time) {
      showToast.error('End time must be after start time');
      return null;
    }

    // Prepare meeting data (always JSON)
    const meetingData = {
      scheduled_date: formData.scheduled_date,
      start_time: formData.start_time,
      end_time: formData.end_time,
      location: formData.location.trim(),
      meeting_type: formData.meeting_type,
      recurrence: formData.recurrence,
      participant_ids: formData.participant_ids,
      agenda: formData.agenda || null
    };

    // Add custom recurrence pattern if recurrence is 'custom'
    if (formData.recurrence === 'custom' && formData.custom_recurrence) {
      const frequencyMap = { 'day': 'daily', 'week': 'weekly', 'month': 'monthly', 'year': 'yearly' };
      const endTypeMap = { 'never': 'never', 'on': 'on_date', 'after': 'after' };
      meetingData.custom_recurrence_pattern = {
        frequency: frequencyMap[formData.custom_recurrence.repeatUnit] || formData.custom_recurrence.repeatUnit,
        interval: formData.custom_recurrence.repeatEvery,
        end_type: endTypeMap[formData.custom_recurrence.endType] || formData.custom_recurrence.endType,
        end_value: formData.custom_recurrence.endType === 'on'
          ? formData.custom_recurrence.endDate
          : formData.custom_recurrence.endType === 'after'
            ? formData.custom_recurrence.occurrences
            : null,
        days_of_week: (formData.custom_recurrence.selectedDays && formData.custom_recurrence.selectedDays.length > 0)
          ? formData.custom_recurrence.selectedDays
          : null,
        day_of_month: null
      };
    }

    return meetingData;
  };

  // Determine if the meeting belongs to a recurring series
  const isMeetingRecurring = Boolean(
    meeting?.recurring_series_id ||
    meeting?.is_recurring ||
    (meeting?.recurrence && meeting?.recurrence !== 'none')
  );

  // Called when the "Update Meeting" button is clicked
  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    const meetingData = buildMeetingData();
    if (!meetingData) return; // Validation failed

    if (isMeetingRecurring) {
      // Show scope-selection dialog for recurring meetings
      setPendingSubmitData(meetingData);
      setShowRecurringUpdateDialog(true);
    } else {
      // Non-recurring: submit immediately
      executeUpdate(meetingData, null);
    }
  };

  // Called after the user picks a scope in the recurring update dialog
  const handleRecurringUpdateConfirm = (updateScope) => {
    setShowRecurringUpdateDialog(false);
    if (pendingSubmitData) {
      executeUpdate(pendingSubmitData, updateScope);
      setPendingSubmitData(null);
    }
  };

  // Performs the actual API call
  const executeUpdate = async (meetingData, updateScope) => {
    setIsLoading(true);
    try {
      // Attach update scope for recurring meetings
      const payload = updateScope ? { ...meetingData, update_scope: updateScope } : meetingData;

      console.log('📤 [EditModal] Submitting meeting data:', {
        ...payload,
        agenda: payload.agenda?.substring(0, 100) + '...'
      });

      const hasNewDocuments = agendaFiles.length > 0;
      console.log(`📧 Skip notification: ${hasNewDocuments} (has ${agendaFiles.length} new documents)`);

      await onSubmit(meeting.id, payload, hasNewDocuments);

      // Upload new agenda documents if any (one by one)
      if (agendaFiles.length > 0) {
        let uploadedCount = 0;
        for (const file of agendaFiles) {
          try {
            await meetingAPI.uploadAgendaDocument(meeting.id, file);
            uploadedCount++;
          } catch (error) {
            console.error(`Failed to upload agenda document ${file.name}:`, error);
            showToast.error(`Failed to upload ${file.name}`);
          }
        }

        if (uploadedCount > 0) {
          try {
            console.log('📧 Sending updated meeting notification with new documents...');
            await meetingAPI.sendNotification(meeting.id, true);
            console.log('✅ Meeting update notification sent successfully');
          } catch (error) {
            console.error('❌ Failed to send meeting update notification:', error);
          }
          showToast.success(`Meeting updated with ${uploadedCount} new document(s)`);
        }
      }
    } catch (error) {
      showToast.error('Failed to update meeting');
    } finally {
      setIsLoading(false);
    }
  };

  const handleParticipantChange = (userId, checked) => {
    // For ongoing meetings, cannot remove already selected participants
    if (isOngoingMeeting && !checked && meeting.participant_ids.includes(userId)) {
      showToast.error('Cannot remove participants from an ongoing meeting');
      return;
    }

    if (checked) {
      setFormData(prev => ({
        ...prev,
        participant_ids: [...prev.participant_ids, userId]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        participant_ids: prev.participant_ids.filter(id => id !== userId)
      }));
    }
  };

  // Handle new user created from AddParticipantModal
  const handleUserCreated = (newUser) => {
    // Add to allUsers list
    setAllUsers(prev => [...prev, newUser]);

    // Auto-select the newly created user as participant
    setFormData(prev => ({
      ...prev,
      participant_ids: [...prev.participant_ids, newUser.id]
    }));

    showToast.success(`${newUser.role === 'vendor' ? 'Vendor' : 'Employee'} "${newUser.full_name}" added and selected as participant`);
  };

  // Handle delete existing agenda document
  const handleDeleteExistingDocument = async (documentIndex) => {
    if (!meeting?.id) return;

    try {
      setIsLoading(true);
      await meetingAPI.deleteAgendaDocument(meeting.id, documentIndex);

      // Remove from local state
      setExistingAgendaDocuments(prev => prev.filter((_, index) => index !== documentIndex));
      showToast.success('Document deleted successfully');

      // Trigger refresh
      window.dispatchEvent(new CustomEvent('meetingUpdated'));
    } catch (error) {
      handleAPIError(error, 'Failed to delete document', true);
    } finally {
      setIsLoading(false);
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  if (!meeting) return null;

  // For upcoming, ongoing, and pending meetings, some fields are locked once scheduled.
  const isFieldDisabled = (fieldName) => {
    // Completed or Cancelled meetings are fully locked
    if (isCompletedMeeting || isCancelledMeeting) return true;

    if (isOngoingMeeting) {
      // Ongoing: lock date, recurrence, start time, meeting type, and location
      const lockedFields = ['scheduled_date', 'recurrence', 'start_time', 'meeting_type', 'location'];
      return lockedFields.includes(fieldName);
    }

    if (isPendingMeeting) {
      // Pending: lock date, recurrence, start time, and end time
      const lockedFields = ['scheduled_date', 'recurrence', 'start_time', 'end_time'];
      return lockedFields.includes(fieldName);
    }

    if (isUpcomingMeeting) {
      // Upcoming: only lock recurrence; date, start time, and end time are editable
      return fieldName === 'recurrence';
    }

    return false;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleModalClose}>
        <DialogContent className="sm:max-w-[600px] max-w-[95vw] mx-auto max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="sticky top-0 z-10 bg-white border-b px-6 py-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <DialogTitle className="flex items-center gap-2">
                  <Edit className="w-5 h-5 text-blue-600" />
                  Edit Meeting {isOngoingMeeting && <span className="text-orange-600 text-sm">(Ongoing)</span>}
                  {isPendingMeeting && <span className="text-amber-600 text-sm">(Pending)</span>}
                </DialogTitle>
                <DialogDescription>
                  {isOngoingMeeting
                    ? "Ongoing meeting: You can edit title, agenda, end time, and invite new participants only. Meeting type, location, date, start time, and recurrence cannot be changed. Already selected participants cannot be removed."
                    : isPendingMeeting
                      ? "Pending meeting: You can edit details and participants before adding MOM."
                      : "Update meeting details, participants, and agenda"
                  }
                </DialogDescription>
              </div>
              <DialogClose className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6  space-y-6">
            <div className="grid grid-cols-1 gap-4">
              {/* Meeting Title */}
              <div className="space-y-2">
                <Label htmlFor="edit-title" className="text-sm font-medium">Meeting Title <span className="text-red-500">*</span></Label>
                <Input
                  id="edit-title"
                  placeholder="Enter meeting title"
                  value={formData.title}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Limit to 100 characters
                    if (value.length <= 100) {
                      setFormData(prev => ({ ...prev, title: value }));
                    }
                  }}
                  className="focus-gold"
                  disabled={isFieldDisabled('title')}
                />
                <p className="text-xs text-gray-500 text-right">
                  {formData.title.length}/100 characters
                </p>
              </div>

              {/* Location and Meeting Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                <div className="space-y-2">
                  <Label htmlFor="edit-meeting_type" className="text-sm font-medium">Meeting Type <span className="text-red-500">*</span></Label>
                  <Select
                    value={formData.meeting_type}
                    onValueChange={handleMeetingTypeChange}
                    disabled={isFieldDisabled('meeting_type')}
                  >
                    <SelectTrigger className="focus-gold disabled:bg-gray-100 disabled:cursor-not-allowed">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in-person">In-Person</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                    </SelectContent>
                  </Select>
                  {!meetingTypeValidation.isValid && (
                    <p className="text-xs text-red-500 mt-1">{meetingTypeValidation.message}</p>
                  )}
                </div>


                <div className="space-y-2">
                  <Label htmlFor="edit-location" className="text-sm font-medium">Location {formData.meeting_type === 'in-person' ? <span className="text-red-500">*</span> : ''}</Label>
                  <Input
                    id="edit-location"
                    placeholder="Enter location"
                    value={formData.location}
                    onChange={handleLocationChange}
                    className="focus-gold disabled:bg-gray-100 disabled:cursor-not-allowed"
                    disabled={formData.meeting_type === 'online' || isFieldDisabled('location')}
                  />

                  {!locationValidation.isValid && formData.meeting_type === 'in-person' && (
                    <p className="text-xs text-red-500 mt-1">{locationValidation.message}</p>
                  )}
                </div>


              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-date" className="text-sm font-medium">Meeting Date</Label>
                  <Input
                    id="edit-date"
                    type="date"
                    value={formData.scheduled_date}
                    min={getTodayDate()}
                    onChange={(e) => setFormData(prev => ({ ...prev, scheduled_date: e.target.value }))}
                    className="focus-gold disabled:bg-gray-100 disabled:cursor-not-allowed"
                    disabled={isFieldDisabled('scheduled_date')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-recurrence" className="text-sm font-medium">Recurrence</Label>
                  <Select
                    value={formData.recurrence}
                    onValueChange={(value) => {
                      if (value === 'custom') {
                        setShowCustomRecurrenceModal(true);
                      } else {
                        setFormData(prev => ({ ...prev, recurrence: value, custom_recurrence: null }));
                      }
                    }}
                    disabled={isFieldDisabled('recurrence')}
                  >
                    <SelectTrigger className="focus-gold disabled:bg-gray-100 disabled:cursor-not-allowed">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {recurrenceOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Show custom recurrence summary */}
                  {formData.recurrence === 'custom' && formData.custom_recurrence && (
                    <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded border border-blue-200">
                      ✓ Custom: Every {formData.custom_recurrence.repeatEvery} {formData.custom_recurrence.repeatUnit}
                      {formData.custom_recurrence.selectedDays?.length > 0 &&
                        ` on ${formData.custom_recurrence.selectedDays.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ')}`
                      }
                      {formData.custom_recurrence.endType === 'after' && ` (${formData.custom_recurrence.occurrences} times)`}
                      {formData.custom_recurrence.endType === 'on' && formData.custom_recurrence.endDate && ` (until ${formData.custom_recurrence.endDate})`}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-start_time" className="text-sm font-medium">Start Time</Label>
                  <Input
                    id="edit-start_time"
                    type="time"
                    value={formData.start_time}
                    min={formData.scheduled_date === getTodayDate() ? new Date().toTimeString().slice(0, 5) : undefined}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                    className="focus-gold disabled:bg-gray-100 disabled:cursor-not-allowed"
                    disabled={isFieldDisabled('start_time')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-end_time" className="text-sm font-medium">End Time</Label>
                  <Input
                    id="edit-end_time"
                    type="time"
                    value={formData.end_time}
                    min={formData.start_time || undefined}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                    className="focus-gold"
                    disabled={isFieldDisabled('end_time')}
                  />
                </div>
              </div>

              {/* Participants */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Participants <span className="text-red-500">*</span></Label>

                {/* Employee info - can select all, but can only CREATE vendors */}
                {currentUser?.role === 'employee' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-800">
                      <strong>Note:</strong> You can add existing users as participants. To add a new participant, you can only create vendors (not employees).
                    </p>
                  </div>
                )}

                <Card className="border border-gray-200">
                  <CardHeader className="py-3 px-4">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Select Participants ({formData.participant_ids.length} selected)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="py-3 px-4 space-y-3">
                    {/* Search Input */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        type="text"
                        placeholder="Search participants by name or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 pr-10"
                      />
                      {searchQuery && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                          onClick={() => setSearchQuery('')}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto">
                      {filteredUsers.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-4 space-y-3">
                          <div className="text-sm text-gray-500">
                            {searchQuery ? 'No users found matching your search' : 'No users available'}
                          </div>
                          {searchQuery && searchQuery.includes('@') && searchQuery.includes('.') && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setShowAddParticipantModal(true);
                                setEmailForNewParticipant(searchQuery.trim());
                              }}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add "{searchQuery}" as New Participant
                            </Button>
                          )}
                        </div>
                      ) : (
                        filteredUsers.map(user => {
                          const availability = getParticipantAvailability(
                            user.id,
                            ongoingMeetings,
                            formData.scheduled_date,
                            formData.start_time,
                            formData.end_time
                          );

                          // Check if this participant is already in the original meeting
                          const wasOriginallySelected = meeting.participant_ids?.includes(user.id);

                          return (
                            <div key={user.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`edit-participant-${user.id}`}
                                checked={formData.participant_ids.includes(user.id)}
                                onCheckedChange={(checked) => handleParticipantChange(user.id, checked)}
                              />
                              <Label
                                htmlFor={`edit-participant-${user.id}`}
                                className={`text-sm font-normal cursor-pointer flex-1 ${wasOriginallySelected && isOngoingMeeting
                                  ? 'text-gray-700 font-medium'
                                  : ''
                                  }`}
                              >
                                <div className="flex flex-col gap-1 w-full">
                                  <div className="flex items-center gap-2">
                                    <span>
                                      {user.full_name || user.name || user.email || 'Unknown'}
                                      {user.email && ` (${user.email})`}
                                    </span>

                                    {/* Badge for originally selected participants in ongoing meeting */}
                                    {wasOriginallySelected && isOngoingMeeting && (
                                      <div className="flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                        <CheckCircle className="w-3 h-3" />
                                        <span className="text-xs font-semibold">Already Joined</span>
                                      </div>
                                    )}

                                    {/* Show warning if participant is in another meeting (informational only) */}
                                    {!wasOriginallySelected && !availability.isAvailable && formData.scheduled_date && formData.start_time && formData.end_time && (
                                      <div className="flex items-center gap-1 text-amber-600">
                                        <AlertTriangle className="w-3 h-3" />
                                        <span className="text-xs font-semibold">In Another Meeting</span>
                                      </div>
                                    )}
                                  </div>
                                  {!availability.isAvailable && availability.conflictingMeetings && availability.conflictingMeetings.length > 0 && (
                                    <div className="text-xs text-amber-600 ml-6 space-y-0.5">
                                      {availability.conflictingMeetings.map((conflictMeeting, idx) => (
                                        <div key={idx} className="flex items-center gap-1">
                                          <span>• In "{conflictMeeting.title}"</span>
                                          <span className="text-gray-600">
                                            ({conflictMeeting.start_time} - {conflictMeeting.end_time})
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </Label>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Conflicts Summary - Informational Warning Only */}
                {conflicts.length > 0 && (
                  <Card className="border border-amber-200 bg-amber-50">
                    <CardHeader className="py-3 px-4">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2 text-amber-800">
                        <AlertTriangle className="w-4 h-4" />
                        {isOngoingMeeting ? 'New Participants Already in Other Meetings' : 'Participants Already in Other Meetings'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="py-3 px-4">
                      <div className="space-y-2">
                        <p className="text-xs text-amber-700 mb-2 italic">
                          Note: These participants are already scheduled in other meetings at the same time. You can still add them to this meeting.
                        </p>
                        {conflicts.map((conflict, index) => (
                          <div key={index} className="text-sm text-amber-700">
                            <div className="font-medium">{conflict.user.full_name}</div>
                            <div className="text-xs text-amber-600 ml-2">
                              Also in: {conflict.conflicts.map(c =>
                                `"${c.title}" (${formatTime(c.start_time)} - ${formatTime(c.end_time)})`
                              ).join(', ')}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Agenda */}
              <div className="space-y-2">
                <Label htmlFor="edit-agenda" className="text-sm font-medium">Agenda</Label>
                <QuillEditor
                  value={formData.agenda}
                  onChange={handleAgendaChange}
                  placeholder="Enter meeting agenda and topics to discuss..."
                  maxLength={500}
                />
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAgendaUploadModal(true)}
                      className="flex items-center gap-2"
                      disabled={isFieldDisabled('agenda')}
                    >
                      <Upload className="w-4 h-4" />
                      {agendaFiles.length > 0
                        ? `Add More Documents (${agendaFiles.length} selected)`
                        : 'Upload Agenda Documents'}
                    </Button>
                  </div>

                  {/* Existing Agenda Documents */}
                  {existingAgendaDocuments.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-xs text-gray-600 font-medium">
                        Existing Documents ({existingAgendaDocuments.length}):
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {existingAgendaDocuments.map((doc, index) => (
                          <div
                            key={`existing-${index}`}
                            className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded border border-blue-200"
                          >
                            <FileText className="w-4 h-4 flex-shrink-0" />
                            <a
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="truncate max-w-[200px] hover:underline"
                              title={doc.filename}
                            >
                              {doc.filename}
                            </a>
                            {!isFieldDisabled('agenda') && (
                              <button
                                type="button"
                                onClick={() => handleDeleteExistingDocument(index)}
                                className="text-red-500 hover:text-red-700 ml-1"
                                title="Delete document"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* New Documents to Attach */}
                  {agendaFiles.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-xs text-gray-600 font-medium">
                        New Documents to Attach ({agendaFiles.length}):
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {agendaFiles.map((file, index) => (
                          <div
                            key={`${file.name}-${file.size}-${index}`}
                            className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-2 py-1 rounded border"
                          >
                            <FileText className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate max-w-[200px]" title={file.name}>
                              {file.name}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setAgendaFiles(prev => prev.filter((_, i) => i !== index))}
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700 flex-shrink-0"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>

          <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Updating...' : 'Update Meeting'}
            </Button>
          </div>
        </DialogContent>

        {/* Add Participant Modal */}
        <AddParticipantModal
          open={showAddParticipantModal}
          onClose={() => {
            setShowAddParticipantModal(false);
            setEmailForNewParticipant('');
          }}
          searchedEmail={emailForNewParticipant}
          onUserCreated={handleUserCreated}
        />

        {/* Agenda Upload Modal */}
        <AgendaUploadModal
          open={showAgendaUploadModal}
          onClose={() => setShowAgendaUploadModal(false)}
          existingFiles={agendaFiles}
          onFileSelect={(files) => {
            setAgendaFiles(prev => [...prev, ...files]);
            showToast.success(`${files.length} document${files.length > 1 ? 's' : ''} attached successfully`);
          }}
        />

        {/* Custom Recurrence Modal */}
        <CustomRecurrenceModal
          open={showCustomRecurrenceModal}
          onClose={() => {
            setShowCustomRecurrenceModal(false);
            // If no custom recurrence was saved, reset to original
            if (!formData.custom_recurrence && meeting?.recurrence !== 'custom') {
              setFormData(prev => ({ ...prev, recurrence: meeting?.recurrence || 'none' }));
            }
          }}
          initialData={formData.custom_recurrence}
          onSave={(customRecurrence) => {
            setFormData(prev => ({ ...prev, recurrence: 'custom', custom_recurrence: customRecurrence }));
            setShowCustomRecurrenceModal(false);
          }}
        />
      </Dialog>

      {/* Recurring Update Scope Dialog */}
      <UpdateRecurringMeetingDialog
        open={showRecurringUpdateDialog}
        onClose={() => {
          setShowRecurringUpdateDialog(false);
          setPendingSubmitData(null);
        }}
        meeting={meeting}
        onConfirm={handleRecurringUpdateConfirm}
        isLoading={isLoading}
        allowFollowingScope={allowFollowingScope}
      />

      {/* Close Modal Confirmation */}
      <ConfirmDialog
        open={closeConfirm.isOpen}
        onOpenChange={closeConfirm.setIsOpen}
        onConfirm={closeConfirm.handleConfirm}
        title="Discard Changes?"
        description="You have unsaved changes to this meeting. Are you sure you want to close without saving?"
        confirmText="Yes, Discard"
        cancelText="Keep Editing"
        variant="warning"
        icon="warning"
      />
    </>
  );
};

export default EditMeetingModal;